import React, { useState, useEffect } from "react";

const technologies = [
  {
    name: "React",
    logo: "https://cdn4.iconfinder.com/data/icons/logos-3/600/React.js_logo-512.png",
    category: "Frontend",
  },
  {
    name: "Node.js",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/Node.js_logo_2015.svg/2560px-Node.js_logo_2015.svg.png",
    category: "Backend",
  },
  {
    name: "Python",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Python-logo-notext.svg/1200px-Python-logo-notext.svg.png",
    category: "Backend",
  },
  {
    name: "JavaScript",
    logo: "https://upload.wikimedia.org/wikipedia/commons/6/6a/JavaScript-logo.png",
    category: "Frontend",
  },
  {
    name: "TypeScript",
    logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTcr9ErjixrOB4brfXICewuiMmzsvkUJ4jlHg&s",
    category: "Frontend",
  },
  {
    name: "MongoDB",
    logo: "https://cdn.iconscout.com/icon/free/png-256/free-mongodb-logo-icon-download-in-svg-png-gif-file-formats--wordmark-programming-langugae-freebies-pack-logos-icons-1175140.png?f=webp",
    category: "Database",
  },
  {
    name: "PostgreSQL",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/29/Postgresql_elephant.svg/640px-Postgresql_elephant.svg.png",
    category: "Database",
  },
  {
    name: "Docker",
    logo: "https://cdn.worldvectorlogo.com/logos/docker.svg",
    category: "DevOps",
  },
  {
    name: "Java",
    logo: "https://upload.wikimedia.org/wikipedia/de/thumb/e/e1/Java-Logo.svg/1200px-Java-Logo.svg.png",
    category: "Backend",
  },
  {
    name: "Android",
    logo: "https://images.vexels.com/media/users/3/139556/isolated/svg/1718a076e29822051df8bcf8b5ce1124.svg",
    category: "Mobile",
  },
  {
    name: "WordPress",
    logo: "https://download.logo.wine/logo/WordPress.com/WordPress.com-Logo.wine.png",
    category: "CMS",
  },
].map((tech, index) => ({ ...tech, id: index }));

const categories = [
  "All",
  ...new Set(technologies.map((tech) => tech.category)),
];

const TechnologiesSection = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    const section = document.getElementById("technologies");
    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  const filteredTechnologies = technologies.filter((tech) => {
    const matchesCategory =
      selectedCategory === "All" || tech.category === selectedCategory;
    const matchesSearch = tech.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <section
      id="technologies"
      className="min-h-screen bg-gradient-to-b from-black to-gray-900 py-16 px-4"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div
          className={`text-center mb-12 transform transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-6">
            Tecnologías
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Trabajamos con las últimas tecnologías del mercado para ofrecer
            soluciones innovadoras y eficientes
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-8 space-y-4">
          <div className="max-w-md mx-auto">
            <input
              type="text"
              placeholder="Buscar tecnología..."
              className="w-full px-4 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-yellow-500 text-black"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Technologies Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {filteredTechnologies.map((tech, index) => (
            <div
              key={tech.id}
              className={`transform transition-all duration-500 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-all duration-300 hover:shadow-xl hover:shadow-yellow-500/10 group">
                <div className="relative h-20 w-20 mx-auto mb-4">
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-white font-semibold mb-1">{tech.name}</h3>
                  <span className="text-sm text-gray-400">{tech.category}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredTechnologies.length === 0 && (
          <div className="text-center text-gray-400 mt-8">
            No se encontraron tecnologías que coincidan con tu búsqueda.
          </div>
        )}
      </div>
    </section>
  );
};

export default TechnologiesSection;
